import { Block, Document, BLOCKS, INLINES, Inline, Text } from '@contentful/rich-text-types'
import { MlRichText } from '@curran-catalog/curran-atomic-library'
import Image from 'next/image'
import { getWeaveSpecificationsNodes } from '@templates/tm-product-detail/utils'
import { ContentfulObjFlooringWeave } from 'types'

const isPdfUri = (node: Block | Inline | Text): boolean => {
  if (node.nodeType !== INLINES.HYPERLINK) return false
  const uri = (node as Inline).data?.uri
  return typeof uri === 'string' && uri.toLowerCase().endsWith('.pdf')
}

const isPdfLinkParagraph = (node: Block | Text): boolean => {
  if (node.nodeType !== BLOCKS.PARAGRAPH) return false
  const meaningful = (node as Block).content.filter(
    (child) => !(child.nodeType === 'text' && 'value' in child && !(child as Text).value.trim()),
  )
  return meaningful.length > 0 && meaningful.every(isPdfUri)
}

export const groupPdfParagraphs = (content: Block[]): Block[] => {
  const result: Block[] = []
  let pdfBuffer: Block[] = []

  const flush = () => {
    if (!pdfBuffer.length) return
    result.push({
      nodeType: BLOCKS.PARAGRAPH,
      data: { pdfGroup: true },
      content: pdfBuffer.flatMap((p) => (p as Block).content.filter((c) => c.nodeType === INLINES.HYPERLINK)),
    } as unknown as Block)
    pdfBuffer = []
  }

  for (const node of content) {
    if (isPdfLinkParagraph(node as Block | Text)) {
      pdfBuffer.push(node)
    } else {
      flush()
      result.push(node)
    }
  }
  flush()
  return result
}

export const SpecificationsContent = ({
  product,
  showDivider = false,
}: {
  product: ContentfulObjFlooringWeave
  showDivider?: boolean
}) => {
  const additionalSpecsHeading = {
    nodeType: BLOCKS.HEADING_3,
    data: { specSectionHeading: true },
    content: [{ nodeType: 'text', value: 'Additional Specs', marks: [], data: {} }],
  }

  const leftDocument: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: getWeaveSpecificationsNodes(product) as unknown as Document['content'],
  }

  const hasAdditionalSpecs = Boolean(product.specificationInformation?.content?.length)

  const rightDocument: Document | null = hasAdditionalSpecs
    ? {
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: groupPdfParagraphs([
          additionalSpecsHeading as unknown as Block,
          ...product.specificationInformation!.content,
        ]) as unknown as Document['content'],
      }
    : null

  return (
    <div className={`tw-grid tw-gap-8 ${hasAdditionalSpecs ? 'md:tw-grid-cols-2' : ''}`}>
      <MlRichText
        text={leftDocument}
        customRenderNode={{
          [BLOCKS.HEADING_3]: (node, children) => {
            if (!node.data?.specSectionHeading) return <h3>{children}</h3>
            return <h3 className="tw-text-xl tw-font-medium tw-mt-2 first:tw-mt-0 tw-mb-3">{children}</h3>
          },
          [BLOCKS.PARAGRAPH]: (node, children) => {
            if (node.data?.specRow) {
              const labelNode = node.content?.[0]
              const valueNode = node.content?.[1]
              const label =
                labelNode && 'value' in labelNode && typeof labelNode.value === 'string' ? labelNode.value : ''
              const value =
                valueNode && 'value' in valueNode && typeof valueNode.value === 'string' ? valueNode.value : ''
              return (
                <div
                  className="tw-flex tw-flex-col md:tw-grid"
                  style={{
                    gridTemplateColumns: '200px minmax(0, 1fr)',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{label}</div>
                  <div>{value}</div>
                </div>
              )
            }
            return <p className="tw-mb-3 tw-leading-relaxed">{children}</p>
          },
        }}
      />
      {rightDocument && (
        <div className={showDivider ? 'specs-right-col' : ''}>
          <MlRichText
            text={rightDocument}
            customRenderNode={{
              [BLOCKS.HEADING_3]: (node, children) => {
                if (!node.data?.specSectionHeading) return <h3>{children}</h3>
                return <h3 className="tw-text-xl tw-font-medium tw-mt-2 first:tw-mt-0 tw-mb-3">{children}</h3>
              },
              [BLOCKS.PARAGRAPH]: (node, children) => {
                const pdfCount = node.content.filter(isPdfUri).length
                if (pdfCount > 0) {
                  return (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, auto)',
                        gridAutoRows: 'min-content',
                        justifyContent: 'start',
                        alignItems: 'start',
                        width: 'fit-content',
                        gap: '1rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      {children}
                    </div>
                  )
                }
                return <p className="tw-inline">{children}</p>
              },
              [INLINES.HYPERLINK]: (node, children) => {
                const uri: string = node.data?.uri ?? ''
                if (uri.toLowerCase().endsWith('.pdf')) {
                  return (
                    <a
                      href={uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-text-secondary hover:tw-text-link tw-flex tw-flex-col tw-items-center tw-gap-1 tw-no-underline"
                    >
                      <Image
                        src="/images/flooring/pdf_icon_full.png"
                        alt="PDF"
                        width={80}
                        height={80}
                        className="tw-object-contain"
                      />
                      <span className="tw-underline tw-text-center">{children}</span>
                    </a>
                  )
                }
                return (
                  <a
                    href={uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-text-secondary hover:tw-text-link tw-underline"
                  >
                    {children}
                  </a>
                )
              },
            }}
          />
        </div>
      )}
    </div>
  )
}
